import { ROUTE_LOGISTIC_SINGLE_COURIER } from '@@partner:config/routes'
import { convertToInt, formatDate } from '@@partner:helpers/util'
import { VehicleIncident } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'
import React from 'react'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { Avatar, Button, Col, Grid, Modal, Panel, Row, Tag } from 'rsuite'

import './VehicleIncidentElement.scss'

type VehicleIncidentElementProps = {
  vehicleIncident: VehicleIncident
  className: string
}

const mapIncidentType = (type: string) => {
  switch (type) {
  case 'accident':
    return 'ДТП'
  case 'damage':
    return 'Повреждение'
  case 'breakdown':
    return 'Поломка'
  case 'theft':
    return 'Кража'
  default:
    return 'Неизвестный тип'
  }
}

export default function VehicleIncidentElement ({
  vehicleIncident,
  className
}: VehicleIncidentElementProps) {
  const { brandSlug } = useParams<any>()
  const history = useHistory()
  const [ selectedImage, setSelectedImage ] = React.useState<string | null>(null)
  const [ currentImageIndex, setCurrentImageIndex ] = React.useState<number>(0)

  const handleNavToCourier = (courierId: GQL_ID) => {
    history.push(generatePath(ROUTE_LOGISTIC_SINGLE_COURIER, {
      brandSlug,
      courierId
    }))
  }

  const openImageModal = (url: string) => {
    // Находим индекс текущего изображения
    const fileIndex = vehicleIncident.files?.findIndex(file => file.url === url) || 0
    setCurrentImageIndex(fileIndex)
    setSelectedImage(url)
  }
  
  const closeImageModal = () => {
    setSelectedImage(null)
  }
  
  const handlePrevImage = () => {
    if (!vehicleIncident.files || !vehicleIncident.files.length) return
    
    const newIndex = (currentImageIndex - 1 + vehicleIncident.files.length) % vehicleIncident.files.length
    setCurrentImageIndex(newIndex)
    if (vehicleIncident.files[newIndex].url) {
      setSelectedImage(vehicleIncident.files[newIndex].url)
    }
  }
  
  const handleNextImage = () => {
    if (!vehicleIncident.files || !vehicleIncident.files.length) return
    
    const newIndex = (currentImageIndex + 1) % vehicleIncident.files.length
    setCurrentImageIndex(newIndex)
    if (vehicleIncident.files[newIndex].url) {
      setSelectedImage(vehicleIncident.files[newIndex].url)
    }
  }

  const startDate = vehicleIncident.courierWorkShift?.plannedStartAt ? new Date(convertToInt(vehicleIncident.courierWorkShift.plannedStartAt)) : null
  const endDate = vehicleIncident.courierWorkShift?.plannedFinishAt ? new Date(convertToInt(vehicleIncident.courierWorkShift.plannedFinishAt)) : null
  

  return (
    <div className={`VehicleIncidentElement ${className}`}>
      <Panel
        bordered
        header={
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <Tag color="blue">{mapIncidentType(vehicleIncident.incident_type)}</Tag>
              <span className="ml-2">{formatDate(new Date(vehicleIncident.occurred_at))}</span>
            </span>
            
          </div>
        }
      >
        {vehicleIncident.courier && (
          <div 
            className="VehicleIncidentElement__CourierCard my-5"
            onClick={() => vehicleIncident.courier?.id && handleNavToCourier(vehicleIncident.courier.id)}
          >
            <Avatar circle size="md" style={{ backgroundColor: '#3498ff' }}>
              {vehicleIncident.courier.fullName?.charAt(0).toUpperCase() || 'К'}
            </Avatar>
            <div className="VehicleIncidentElement__CourierInfo">
              <div>Курьер</div>
              <div className="VehicleIncidentElement__CourierName">
                {vehicleIncident.courier.fullName}
              </div>
              <a 
                href={`tel:+${vehicleIncident.courier.phone}`}
                className="VehicleIncidentElement__CourierPhone"
                onClick={(e) => e.stopPropagation()}
              >
                +{vehicleIncident.courier.phone}
              </a>
            </div>
          </div>
        )}
        { vehicleIncident.courierWorkShift && (
          <div className="VehicleIncidentElement__CourierCard my-5">
            <div>
              <div>Смена курьера:</div>
              <div className="VehicleIncidentElement__CourierName">
                { startDate && <div>{ formatDate(startDate) }{ endDate && '-' + formatDate(endDate) }</div> }
                { vehicleIncident.courierWorkShift.startedAt && <div>Начата: {formatDate(new Date(vehicleIncident.courierWorkShift.startedAt))}</div> }
                { vehicleIncident.courierWorkShift.finishedAt && <div>Закончена: {formatDate(new Date(convertToInt(vehicleIncident.courierWorkShift.finishedAt)))}</div>}
              </div>
            </div>
          </div>
        )}
        <div className="mb-3">
          <strong>Описание:</strong>
          <div className="pl-3 text-lg">{vehicleIncident.description}</div>
        </div>
        
        {vehicleIncident.comment && (
          <div>
            <strong>Комментарий администратора:</strong>
            <div className="pl-3">{vehicleIncident.comment}</div>
          </div>
        )}
        
        {vehicleIncident.files && vehicleIncident.files.length > 0 && (
          <Grid fluid className="mt-4">
            <Row>
              <Col xs={24}>
                <div className="VehicleIncidentElement__ImagesTitle">
                  <strong>Фотографии инцидента:</strong>
                </div>
                <div className="VehicleIncidentElement__ImagesGrid">
                  {vehicleIncident.files.map((file, index) => (
                    <div 
                      key={`image:${index}`} 
                      className="VehicleIncidentElement__ImageThumbnail"
                      onClick={() => file.url && openImageModal(file.url)}
                    >
                      <img src={file.url} alt={`Фото ${index + 1}`} />
                      <div className="VehicleIncidentElement__ImageOverlay">
                        <span className="VehicleIncidentElement__ImageZoom">🔍</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Grid>
        )}
        
        <div className="text-right text-muted small mt-2">
          {vehicleIncident.created_at && (
            <div>Создано: {formatDate(new Date(vehicleIncident.created_at))}</div>
          )}
          {vehicleIncident.updated_at && vehicleIncident.updated_at !== vehicleIncident.created_at && (
            <div>Обновлено: {formatDate(new Date(vehicleIncident.updated_at))}</div>
          )}
        </div>
      </Panel>

      {/* Модальное окно для просмотра изображений */}
      <Modal 
        size="lg" 
        open={!!selectedImage} 
        onClose={closeImageModal} 
        className="VehicleIncidentElement__ImageModal"
      >
        <Modal.Header>
          <Modal.Title>
            Просмотр изображения {vehicleIncident.files && vehicleIncident.files.length > 0 ? 
              `${currentImageIndex + 1} из ${vehicleIncident.files.length}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="VehicleIncidentElement__FullImage">
            {selectedImage && <img src={selectedImage} alt="Увеличенное изображение" />}
            
            {vehicleIncident.files && vehicleIncident.files.length > 1 && (
              <div className="VehicleIncidentElement__ImageNavigation">
                <Button 
                  onClick={handlePrevImage} 
                  appearance="ghost"
                  className="VehicleIncidentElement__NavButton VehicleIncidentElement__NavButton--prev"
                >
                  <span className="VehicleIncidentElement__NavButtonDesktop">&laquo; Назад</span>
                  <span className="VehicleIncidentElement__NavButtonMobile">&laquo;</span>
                </Button>
                <Button 
                  onClick={handleNextImage} 
                  appearance="ghost"
                  className="VehicleIncidentElement__NavButton VehicleIncidentElement__NavButton--next"
                >
                  <span className="VehicleIncidentElement__NavButtonDesktop">Вперед &raquo;</span>
                  <span className="VehicleIncidentElement__NavButtonMobile">&raquo;</span>
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            onClick={closeImageModal} 
            appearance="ghost" 
            className="VehicleIncidentElement__CloseButton"
          >
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}